import { ORIGINAL_ID, audienceVersions, effectiveActions } from '@shared/campaignTests/audienceTest'
import type { VersionId } from '@shared/campaignTests/audienceTest'
import type { ActionKey, ElementFact, ElementId, ResolutionReport, TargetDescriptor } from '@shared/editor/model'
import { matchesUrlCriteria } from '@shared/urlCriteria'
import { stripHtml } from '@shared/services/text'
import { campaignActions, originalAudience, originalLayerEntries } from './campaignEdit'
import type { ActionEntry, StoredCampaign, StoredPageRule } from './campaignEdit'
import { deriveEditorView, sameTarget } from './editorView'
import type { GroupCatalogEntry } from './groups'
import { serializeSubstitutions } from './matrixFindReplace'

const nonTextTags = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
  'audio',
  'canvas',
  'iframe',
  'object',
  'video',
  'select',
  'textarea',
  'svg',
  'image'
])

export function supportsMatrixText(tag: string): boolean {
  return !nonTextTags.has(tag.toLowerCase())
}

export type MatrixCandidate = ElementFact & { target?: TargetDescriptor }
export type MatrixRow = {
  id: string
  element: ElementId | null
  label: string
  selector: string | null
  target: TargetDescriptor | null
  original: string
  status: 'resolved' | 'missing' | 'invalid-selector' | 'pending'
  personalized: boolean
}
export type MatrixColumn = {
  id: string
  audienceId?: string
  versionId?: VersionId
  audienceName?: string
  tested?: boolean
  locked?: boolean
  name: string
  groupId: string
  groupName: string
  readOnly: boolean
  count: string
}
export type MatrixCell = {
  value: string
  inherited?: boolean
  inheritedActions?: ActionKey[]
  format: 'text' | 'find-replace'
  editable: boolean
  editOnPage: boolean
  editAction: ActionKey | null
  actions: ActionKey[]
}
export type Matrix = {
  rows: MatrixRow[]
  columns: MatrixColumn[]
  cells: Map<string, Map<string, MatrixCell>>
  counts: { personalized: number; total: number; label: string }
}

export function filterMatrix(
  matrix: Matrix,
  filters: { changedOnly: boolean; audiences: readonly string[] | null; elements: readonly string[] | null }
): Matrix {
  const audiences = filters.audiences === null ? null : new Set(filters.audiences)
  const elements = filters.elements === null ? null : new Set(filters.elements)
  const rows = matrix.rows.filter(
    (row) => (!filters.changedOnly || row.personalized) && (!elements || elements.has(row.id))
  )
  const columns = matrix.columns.filter((column) => column.readOnly || !audiences || audiences.has(column.id))
  const cells = new Map<string, Map<string, MatrixCell>>()
  const counts = new Map<string, number>()
  let personalized = 0
  for (const row of rows) {
    const rowCells = new Map<string, MatrixCell>()
    for (const column of columns) {
      const cell = matrix.cells.get(row.id)?.get(column.id)
      if (cell) {
        rowCells.set(column.id, cell)
        if (!column.readOnly && cell.actions.length) {
          personalized++
          counts.set(column.id, (counts.get(column.id) ?? 0) + 1)
        }
      }
    }
    cells.set(row.id, rowCells)
  }
  const total = rows.length * columns.filter((column) => !column.readOnly).length
  return {
    rows,
    columns: columns.map((column) => ({
      ...column,
      count: column.readOnly ? '' : `${counts.get(column.id) ?? 0}/${rows.length}`
    })),
    cells,
    counts: { personalized, total, label: `${personalized} of ${total} cells personalized` }
  }
}
export type MatrixInput = {
  campaign: StoredCampaign
  groups: GroupCatalogEntry[]
  pageUrl: string
  report: ResolutionReport | null
  candidates: readonly MatrixCandidate[]
  originals: ReadonlyMap<ElementId, string>
  unavailableElements?: ReadonlySet<ElementId>
  isAudienceLocked?: (audienceId: string) => boolean
}

export type MatrixSelectionCell = { audienceId: string; versionId?: VersionId; elementId: string }

export function matrixColumnId(audienceId: string, versionId?: VersionId): string {
  return versionId ? `${audienceId}:${versionId}` : audienceId
}

export function matrixCellPlaceholder(column: MatrixColumn): string {
  return column.tested && column.versionId !== 'A' ? 'Same as Control' : 'Same as Original page'
}

export function matrixColumnSelection(column: MatrixColumn, elementId: string): MatrixSelectionCell {
  return {
    audienceId: column.audienceId ?? column.id,
    ...(column.tested ? { versionId: column.versionId } : {}),
    elementId
  }
}

export function matrixSelectionKey(cell: MatrixSelectionCell): string {
  return JSON.stringify([cell.elementId, matrixColumnId(cell.audienceId, cell.versionId)])
}

export function selectableMatrixCell(matrix: Matrix, cell: MatrixSelectionCell): boolean {
  return (
    matrix.columns.some(
      (column) => column.id === matrixColumnId(cell.audienceId, cell.versionId) && !column.readOnly && !column.locked
    ) && matrix.cells.get(cell.elementId)?.get(matrixColumnId(cell.audienceId, cell.versionId))?.editable === true
  )
}

export function matrixSelectionRectangle(
  matrix: Matrix,
  anchor: MatrixSelectionCell,
  end: MatrixSelectionCell
): MatrixSelectionCell[] {
  const startRow = matrix.rows.findIndex((row) => row.id === anchor.elementId)
  const endRow = matrix.rows.findIndex((row) => row.id === end.elementId)
  const startColumn = matrix.columns.findIndex(
    (column) => column.id === matrixColumnId(anchor.audienceId, anchor.versionId)
  )
  const endColumn = matrix.columns.findIndex((column) => column.id === matrixColumnId(end.audienceId, end.versionId))
  if (Math.min(startRow, endRow, startColumn, endColumn) < 0) {
    return []
  }
  return matrix.rows.slice(Math.min(startRow, endRow), Math.max(startRow, endRow) + 1).flatMap((row) =>
    matrix.columns
      .slice(Math.min(startColumn, endColumn), Math.max(startColumn, endColumn) + 1)
      .map((column) => matrixColumnSelection(column, row.id))
      .filter((cell) => selectableMatrixCell(matrix, cell))
  )
}

export function toggleMatrixSelection(
  selection: readonly MatrixSelectionCell[],
  cell: MatrixSelectionCell
): MatrixSelectionCell[] {
  const key = matrixSelectionKey(cell)
  return selection.some((selected) => matrixSelectionKey(selected) === key)
    ? selection.filter((selected) => matrixSelectionKey(selected) !== key)
    : [...selection, cell]
}

export function pageForTarget(target: { rule: StoredPageRule }, visitedUrls: readonly string[]): string | null {
  for (let index = visitedUrls.length - 1; index >= 0; index--) {
    const url = visitedUrls[index]
    if (url && matchesUrlCriteria(target.rule, url)) {
      return url
    }
  }
  const exactPath = target.rule.operator === 'equals' || (!target.rule.operator && !target.rule.path.includes('*'))
  if (!exactPath) {
    return null
  }
  const path = `/${target.rule.path.replace(/^\/+/, '')}`
  for (let index = visitedUrls.length - 1; index >= 0; index--) {
    const visited = visitedUrls[index]
    if (!visited) {
      continue
    }
    const url = new URL(visited)
    if (matchesUrlCriteria({ ...target.rule, path: url.pathname }, visited)) {
      return new URL(path, url.origin).href
    }
  }
  return target.rule.domain === '*' ? null : `https://${target.rule.domain}${path}`
}

function actionCell(entries: ActionEntry[]): MatrixCell {
  const actions = entries.map((entry) => entry.key)
  const hasNonText = entries.some(({ action }) =>
    Object.keys(action.modifications ?? {}).some((kind) => kind !== 'text')
  )
  const contentEntries = entries.filter(
    ({ action }) => action.modifications?.findReplace?.length || action.modifications?.text
  )
  const replacements = entries.flatMap(({ action }) => action.modifications?.findReplace ?? [])
  if (replacements.length) {
    const editable =
      contentEntries.length === 1 &&
      !replacements.some(({ find, replace }) => /[\r\n]/.test(find) || /[\r\n]/.test(replace))
    return {
      value: serializeSubstitutions(replacements),
      format: 'find-replace',
      editable,
      editOnPage: true,
      editAction: editable ? (contentEntries[0]?.key ?? null) : null,
      actions
    }
  }
  const textEntries = entries.filter(
    ({ action }) => typeof action.modifications?.text === 'string' && action.modifications.text !== ''
  )
  const entry = textEntries.at(-1)
  const text = entry?.action.modifications?.text ?? ''
  const markup = /<!--|<!doctype|<\/?[a-z][^>]*>/i.test(text)
  // Decode entities separately so plain copy keeps its original line endings.
  const value = markup
    ? stripHtml(text)
    : text.replace(/&(?:#x[\da-f]+|#\d+|[a-z][\da-z]*);?/gi, (entity) => stripHtml(entity))
  const editable = textEntries.length <= 1 && !markup
  return {
    value,
    format: 'text',
    editable,
    editOnPage: hasNonText || !editable,
    editAction: editable ? (entry?.key ?? actions[0] ?? null) : null,
    actions
  }
}

export function buildMatrix({
  campaign,
  groups,
  pageUrl,
  report,
  candidates,
  originals,
  unavailableElements,
  isAudienceLocked
}: MatrixInput): Matrix {
  const original = originalAudience(campaign)
  const entries = [
    ...(original?.test?.versions.flatMap((version) =>
      version.id === 'A' ? [] : originalLayerEntries(campaign, version.id)
    ) ?? []),
    ...campaignActions({
      ...campaign,
      variants: campaign.variants.map((audience) => ({
        ...audience,
        actions: [
          ...audience.actions,
          ...(audience.test?.versions.flatMap((version) => (version.id === 'A' ? [] : version.actions)) ?? [])
        ]
      }))
    })
  ].filter(({ action }) => action.page.some((rule) => matchesUrlCriteria(rule, pageUrl)))
  const byKey = new Map(entries.map((entry) => [entry.key, entry]))
  const entryOrder = new Map(entries.map((entry, index) => [entry.key, index]))
  const facts = new Map<ElementId, MatrixCandidate>(
    candidates
      .filter((candidate) => !unavailableElements?.has(candidate.id))
      .map((candidate) => [candidate.id, candidate])
  )
  for (const fact of report?.elements ?? []) {
    if (!unavailableElements?.has(fact.id)) {
      facts.set(fact.id, { ...facts.get(fact.id), ...fact })
    }
  }
  const elementEntries = new Map<ElementId, Map<ActionKey, ActionEntry>>()
  const represented = new Set<ActionKey>()
  const firstElement = new Map<ActionKey, ElementId>()
  const sharedActions = new Set<ActionKey>()
  for (const resolution of [...(report?.inventory ?? []), ...(report?.visible ?? [])]) {
    const entry = byKey.get(resolution.key)
    if (!entry) {
      continue
    }
    for (const id of resolution.elements) {
      if (!facts.has(id)) {
        continue
      }
      const first = firstElement.get(entry.key)
      if (first && first !== id) {
        sharedActions.add(entry.key)
      } else {
        firstElement.set(entry.key, id)
      }
      let actions = elementEntries.get(id)
      if (!actions) {
        actions = new Map()
        elementEntries.set(id, actions)
      }
      actions.set(entry.key, entry)
      represented.add(entry.key)
    }
  }
  const included = new Set([...candidates.map((candidate) => candidate.id), ...elementEntries.keys()])
  const rows: MatrixRow[] = []
  const rowEntries = new Map<string, ActionEntry[]>()
  const textlessRows = new Set<string>()
  for (const fact of [...facts.values()].filter((item) => included.has(item.id)).sort((a, b) => a.order - b.order)) {
    const actions = [...(elementEntries.get(fact.id)?.values() ?? [])]
    if (!supportsMatrixText(fact.tag)) {
      if (!actions.length) {
        continue
      }
      textlessRows.add(fact.id)
    }
    const action = actions.find((entry) => !sharedActions.has(entry.key))?.action
    const target =
      fact.target ??
      (action
        ? { selector: action.selector ?? '', ...(action.targeting ? { targeting: action.targeting } : {}) }
        : null)
    rows.push({
      id: fact.id,
      element: fact.id,
      label: fact.label,
      selector: target?.selector ?? null,
      target,
      original: (originals.get(fact.id) ?? '').trim(),
      status: 'resolved',
      personalized: actions.length > 0
    })
    rowEntries.set(
      fact.id,
      actions.sort((a, b) => (entryOrder.get(a.key) ?? 0) - (entryOrder.get(b.key) ?? 0))
    )
  }
  const unresolved: MatrixRow[] = []
  for (const entry of entries) {
    if (represented.has(entry.key)) {
      continue
    }
    const target: TargetDescriptor = {
      selector: entry.action.selector ?? '',
      ...(entry.action.targeting ? { targeting: entry.action.targeting } : {})
    }
    const resolution = report?.inventory.find((item) => item.key === entry.key)
    const captured = rows.filter((row) => row.target && sameTarget(row.target, target))
    if (captured.length) {
      for (const row of captured) {
        const actions = rowEntries.get(row.id)
        actions?.push(entry)
        actions?.sort((a, b) => (entryOrder.get(a.key) ?? 0) - (entryOrder.get(b.key) ?? 0))
        row.personalized = true
      }
      if (captured.length > 1) {
        sharedActions.add(entry.key)
      }
      continue
    }
    const existing = unresolved.find((row) => row.target && sameTarget(row.target, target))
    if (existing) {
      rowEntries.get(existing.id)?.push(entry)
      continue
    }
    const status = resolution?.status === 'invalid-selector' ? 'invalid-selector' : report ? 'missing' : 'pending'
    const row: MatrixRow = {
      id: `target:${entry.key}`,
      element: null,
      label: target.selector,
      selector: target.selector,
      target,
      original: '',
      status,
      personalized: true
    }
    unresolved.push(row)
    rowEntries.set(row.id, [entry])
  }
  rows.push(...unresolved)
  const view = deriveEditorView(campaign, { groups, pageUrl, report, audience: { kind: 'original' }, selection: null })
  const columns: MatrixColumn[] = [
    {
      id: ORIGINAL_ID,
      name: original ? 'Original page · Control' : 'Original page',
      ...(original ? { versionId: 'A' as const } : {}),
      groupId: '',
      groupName: '',
      readOnly: true,
      count: ''
    }
  ]
  // The original page's variants apply to everyone beneath the audiences, so they sit beside Control.
  for (const version of original ? audienceVersions(original, 'Original page') : []) {
    if (version.versionId !== 'A') {
      columns.push({
        id: matrixColumnId(ORIGINAL_ID, version.versionId),
        audienceId: ORIGINAL_ID,
        versionId: version.versionId,
        audienceName: 'Original page',
        tested: true,
        locked: isAudienceLocked?.(ORIGINAL_ID) ?? false,
        name: version.label,
        groupId: ORIGINAL_ID,
        groupName: 'Original page',
        readOnly: false,
        count: ''
      })
    }
  }
  for (const section of view.sections) {
    for (const audience of section.rows) {
      const stored = campaign.variants.find((variant) => variant.id === audience.id)
      if (!stored) {
        continue
      }
      for (const version of audienceVersions(stored, audience.name)) {
        columns.push({
          id: matrixColumnId(audience.id, stored.test ? version.versionId : undefined),
          audienceId: audience.id,
          versionId: version.versionId,
          audienceName: audience.name,
          tested: Boolean(stored.test),
          locked: isAudienceLocked?.(audience.id) ?? false,
          name: version.label,
          groupId: section.key,
          groupName: section.name,
          readOnly: false,
          count: ''
        })
      }
    }
  }
  const columnAudience = (column: MatrixColumn) =>
    column.audienceId === ORIGINAL_ID ? original : campaign.variants.find((variant) => variant.id === column.audienceId)
  const effectiveByColumn = new Map(
    columns.flatMap((column) => {
      const audience = columnAudience(column)
      return audience
        ? [
            [
              column.id,
              new Map(effectiveActions(audience, column.versionId ?? 'A').map((action) => [action.id, action]))
            ] as const
          ]
        : []
    })
  )
  const cells = new Map<string, Map<string, MatrixCell>>()
  const personalizedByAudience = new Map<string, number>()
  let personalized = 0
  for (const row of rows) {
    const entriesByAudience = new Map<string, ActionEntry[]>()
    for (const entry of rowEntries.get(row.id) ?? []) {
      const actions = entriesByAudience.get(entry.audience.id)
      if (actions) {
        actions.push(entry)
      } else {
        entriesByAudience.set(entry.audience.id, [entry])
      }
    }
    const rowCells = new Map<string, MatrixCell>()
    for (const column of columns) {
      const audienceEntries = entriesByAudience.get(column.audienceId ?? column.id) ?? []
      const effective = effectiveByColumn.get(column.id)
      const versionEntries = audienceEntries.flatMap((entry) => {
        const action = effective?.get(entry.action.id)
        return action ? [{ ...entry, action: column.versionId === 'A' ? entry.action : action }] : []
      })
      const cell: MatrixCell = column.readOnly
        ? {
            value: row.original,
            format: 'text' as const,
            editable: false,
            editOnPage: false,
            editAction: null,
            actions: []
          }
        : actionCell(versionEntries)
      if (column.tested && column.versionId !== 'A') {
        const audience = columnAudience(column)
        const baselineIds = new Set(audience?.actions.map((action) => action.id))
        cell.inheritedActions = audienceEntries
          .filter((entry) => baselineIds.has(entry.action.id))
          .map((entry) => entry.key)
        const version = audience?.test?.versions.find((item) => item.id === column.versionId)
        const ownContent =
          version &&
          version.id !== 'A' &&
          (version.actions.some(
            (action) =>
              versionEntries.some((entry) => entry.action.id === action.id) &&
              (action.modifications?.text !== undefined || action.modifications?.findReplace !== undefined)
          ) ||
            version.overrides.some(
              (override) =>
                audienceEntries.some((entry) => entry.action.id === override.actionId) &&
                ('skip' in override ||
                  override.fields.some((field) => field.path[0] === 'text' || field.path[0] === 'findReplace'))
            ))
        cell.inherited = !ownContent
      }
      if (!column.readOnly && (textlessRows.has(row.id) || cell.actions.some((key) => sharedActions.has(key)))) {
        cell.editable = false
        cell.editOnPage = true
        cell.editAction = null
      }
      if (column.locked) {
        cell.editable = false
        cell.editOnPage = false
      }
      rowCells.set(column.id, cell)
      if (cell.actions.length) {
        personalized++
        personalizedByAudience.set(column.id, (personalizedByAudience.get(column.id) ?? 0) + 1)
      }
    }
    cells.set(row.id, rowCells)
  }
  for (const column of columns) {
    if (!column.readOnly) {
      column.count = `${personalizedByAudience.get(column.id) ?? 0}/${rows.length}`
    }
  }
  const total = rows.length * (columns.length - 1)
  return {
    rows,
    columns,
    cells,
    counts: { personalized, total, label: `${personalized} of ${total} cells personalized` }
  }
}
